import { createAsyncThunk } from '@reduxjs/toolkit';
import { axiosAdmin, axiosFeedback } from '../../../api/axios'; // adapte l'import si besoin
import { ADMIN_AUTH_PATHS, ADMIN_DOCTORS_PATHS, ADMIN_FEEDBACK_PATHS } from '../../../api/endpoints';

// Mapping Status UI <-> API
// UI: pending | confirmed | rejected
// API: pending | approved  | rejected
const toApiStatus = (uiStatus) => (uiStatus === 'confirmed' ? 'approved' : uiStatus);
const toUiStatus = (apiStatus) => (apiStatus === 'approved' ? 'confirmed' : apiStatus);

const getErrorMessage = (err) =>
  err?.response?.data?.message || err?.message || 'Une erreur est survenue';

const normalizeList = (arr = []) =>
  arr.map((d) => ({ ...d, status: toUiStatus(d.status) }));

export const initAuth = createAsyncThunk(
  'auth/initAuth',
  async (_, { rejectWithValue }) => {
    try {
      const res = await axiosAdmin.get(ADMIN_AUTH_PATHS.ME);
      // Réponse : { statusCode: 200, message: 'Authenticated', data: { user: {...} } }
      const user = res?.data?.data?.user ?? null;
      return user; // ← retourner directement le user
    } catch (err) {
      return rejectWithValue(null);
    }
  }
);

export const loginAdmin = createAsyncThunk(
  'auth/loginAdmin',
  async ({ email, password }, { rejectWithValue }) => {
    try {
      const res = await axiosAdmin.post(ADMIN_AUTH_PATHS.LOGIN, { email, password });
      return res?.data?.data ?? null;
    } catch (err) {
      return rejectWithValue(getErrorMessage(err));
    }
  }
);

export const logoutAdmin = createAsyncThunk(
  'auth/logoutAdmin',
  async (_, { rejectWithValue }) => {
    try {
      // ✅ DELETE, pas POST
      await axiosAdmin.delete(ADMIN_AUTH_PATHS.LOGOUT, {
        withCredentials: true,
      });

      return true;
    } catch (err) {
      return rejectWithValue(getErrorMessage(err));
    }
  }
);

export const fetchDoctors = createAsyncThunk(
  'doctors/fetchDoctors',
  async ({ status } = {}, { rejectWithValue }) => {
    try {
      const params = {};
      if (status) params.status = toApiStatus(status);
      
      const res = await axiosAdmin.get(ADMIN_DOCTORS_PATHS.LIST, { params });
      
      
      const payload = res?.data || {};
      
      // ✅ L'API retourne { statusCode, message, data: [...], count }
      const rawItems = payload.data || [];
      
      const items = Array.isArray(rawItems) 
        ? rawItems.map((d) => ({
            ...d,
            // ✅ Normaliser le status : approved → confirmed
            status: toUiStatus(d.status),
            // ✅ S'assurer que l'ID est une string
            id: String(d.id ?? d._id ?? ''),
          }))
        : [];
      
      const total = payload.count ?? items.length;
      
      
      return { items, total };
    } catch (err) {
      console.error('❌ [fetchDoctors] Error:', err);
      return rejectWithValue(getErrorMessage(err));
    }
  }
);

// GET /doctors/pending
export const fetchPendingDoctors = createAsyncThunk(
  'doctors/fetchPendingDoctors',
  async (_, { rejectWithValue }) => {
    try {
      const res = await axiosAdmin.get(ADMIN_DOCTORS_PATHS.PENDING);
      const payload = res?.data || {};
      const items = Array.isArray(payload.data) ? normalizeList(payload.data) : [];
      const total = payload.count ?? items.length;
      return { items, total };
    } catch (err) {
      return rejectWithValue(getErrorMessage(err));
    }
  }
);

// GET /doctors/:id
export const fetchDoctorById = createAsyncThunk(
  'doctors/fetchDoctorById',
  async (id, { rejectWithValue }) => {
    try {
      const res = await axiosAdmin.get(ADMIN_DOCTORS_PATHS.BY_ID(id));
      const payload = res?.data || {};
      const doc = payload.data ? { ...payload.data, status: toUiStatus(payload.data.status) } : null;
      return doc;
    } catch (err) {
      return rejectWithValue(getErrorMessage(err));
    }
  }
);

// GET /doctors/stats
export const fetchDoctorStats = createAsyncThunk(
  'doctors/fetchDoctorStats',
  async (_, { rejectWithValue }) => {
    try {
      const res = await axiosAdmin.get(ADMIN_DOCTORS_PATHS.STATS);
      const payload = res?.data || {};
      return payload.data ?? null;
    } catch (err) {
      return rejectWithValue(getErrorMessage(err));
    }
  }
);

// PATCH /doctors/:id/validate
export const validateDoctor = createAsyncThunk(
  'doctors/validateDoctor',
  async ({ id, status, rejectionReason }, { rejectWithValue }) => {
    try {
      const body = { status: toApiStatus(status) }; // confirmed -> approved
      if (body.status === 'rejected' && rejectionReason) {
        body.rejectionReason = rejectionReason;
      }
      const res = await axiosAdmin.patch(ADMIN_DOCTORS_PATHS.VALIDATE(id), body);
      const payload = res?.data || {};
      const updated = payload.data
        ? { ...payload.data, status: toUiStatus(payload.data.status) }
        : null;
      return updated;
    } catch (err) {
      return rejectWithValue(getErrorMessage(err));
    }
  }
);


export const createFeedback = createAsyncThunk(
  'feedback/createFeedback',
  async ({ rating, message }, { rejectWithValue }) => {
    try {
      const res = await axiosFeedback.post(ADMIN_FEEDBACK_PATHS.CREATE, {
        rating,
        message,
      });

      return res?.data?.data ?? null;
    } catch (err) {
      return rejectWithValue(getErrorMessage(err));
    }
  }
);

export const fetchFeedbacks = createAsyncThunk(
  'feedback/fetchFeedbacks',
  async (_, { rejectWithValue }) => {
    try {
      const res = await axiosFeedback.get(ADMIN_FEEDBACK_PATHS.LIST);
      const payload = res?.data || {};

      return {
        items: Array.isArray(payload.data) ? payload.data : [],
        total: payload.count ?? payload.data?.length ?? 0,
      };
    } catch (err) {
      return rejectWithValue(getErrorMessage(err));
    }
  }
);

export const fetchMyFeedbacks = createAsyncThunk(
  'feedback/fetchMyFeedbacks',
  async (_, { rejectWithValue }) => {
    try {
      const res = await axiosFeedback.get(ADMIN_FEEDBACK_PATHS.ME);
      const payload = res?.data || {};

      return {
        items: Array.isArray(payload.data) ? payload.data : [],
        total: payload.count ?? payload.data?.length ?? 0,
      };
    } catch (err) {
      return rejectWithValue(getErrorMessage(err));
    }
  }
);

export const fetchFeedbackById = createAsyncThunk(
  'feedback/fetchFeedbackById',
  async (id, { rejectWithValue }) => {
    try {
      const res = await axiosFeedback.get(ADMIN_FEEDBACK_PATHS.BY_ID(id));
      return res?.data?.data ?? null;
    } catch (err) {
      return rejectWithValue(getErrorMessage(err));
    }
  }
);

export const fetchFeedbacksByRole = createAsyncThunk(
  'feedback/fetchFeedbacksByRole',
  async (role, { rejectWithValue }) => {
    try {
      const res = await axiosFeedback.get(ADMIN_FEEDBACK_PATHS.BY_ROLE(role));
      const payload = res?.data || {};

      return {
        items: Array.isArray(payload.data) ? payload.data : [],
        total: payload.count ?? payload.data?.length ?? 0,
      };
    } catch (err) {
      return rejectWithValue(getErrorMessage(err));
    }
  }
);

export const fetchFeedbackAverage = createAsyncThunk(
  'feedback/fetchFeedbackAverage',
  async (_, { rejectWithValue }) => {
    try {
      const res = await axiosFeedback.get(ADMIN_FEEDBACK_PATHS.AVERAGE);
      return res?.data?.data ?? null;
    } catch (err) {
      return rejectWithValue(getErrorMessage(err));
    }
  }
);

export const updateFeedback = createAsyncThunk(
  'feedback/updateFeedback',
  async ({ id, rating, message }, { rejectWithValue }) => {
    try {
      const res = await axiosFeedback.patch(ADMIN_FEEDBACK_PATHS.UPDATE(id), {
        rating,
        message,
      });

      return res?.data?.data ?? null;
    } catch (err) {
      return rejectWithValue(getErrorMessage(err));
    }
  }
);

export const deleteFeedback = createAsyncThunk(
  'feedback/deleteFeedback',
  async (id, { rejectWithValue }) => {
    try {
      await axiosFeedback.delete(ADMIN_FEEDBACK_PATHS.DELETE(id));
      return id;
    } catch (err) {
      return rejectWithValue(getErrorMessage(err));
    }
  }
);